import { useState, useEffect, useCallback } from "react";
import Cookies from "js-cookie";
import { Slide, toast } from "react-toastify";
 
const API = process.env.REACT_APP_API_URL;
const getToken = () => Cookies.get("access_token") || "";
const authHeaders = () => ({ "Content-Type": "application/json", Authorization: "Bearer " + getToken() });
 
export const PAGE_SIZE = 100;
export const PRODUCT_PAGE_SIZE = 10;
export const SEARCH_PAGE_SIZE = 20;
 
export const apiFetch = {
  get: async (path) => {
    const r = await fetch(`${API}${path}`, { headers: authHeaders() });
    const data = await r.json();
    if (!r.ok || data?.detail) throw new Error(data?.detail || `Error ${r.status}`);
    return data;
  },
  post: async (path, body) => {
    const r = await fetch(`${API}${path}`, { method: "POST", headers: authHeaders(), body: JSON.stringify(body) });
    const data = await r.json();
    if (!r.ok || data?.detail) throw new Error(data?.detail || `Error ${r.status}`);
    return data;
  },
  put: async (path, body) => {
    const r = await fetch(`${API}${path}`, { method: "PUT", headers: authHeaders(), body: JSON.stringify(body) });
    const data = await r.json();
    if (!r.ok || data?.detail) throw new Error(data?.detail || `Error ${r.status}`);
    return data;
  },
  delete: async (path) => {
    const r = await fetch(`${API}${path}`, { method: "DELETE", headers: authHeaders() });
    const data = await r.json();
    if (!r.ok || data?.detail) throw new Error(data?.detail || `Error ${r.status}`);
    return data;
  },
};
 
export const buildSearchQuery = (term) =>
  term ? `&column_operator=LIKE&column_value=${encodeURIComponent(term)}` : "";
 
export const fetchAllPages = async (basePath, extraQuery = "") => {
  let page = 1;
  let totalPages = 1;
  let all = [];
  do {
    const sep = basePath.includes("?") ? "&" : "?";
    const r = await apiFetch.get(`${basePath}${sep}page=${page}&size=${PAGE_SIZE}${extraQuery}`);
    all = all.concat(r?.data || []);
    totalPages = r?.pagination_info?.total_pages || 1;
    page += 1;
  } while (page <= totalPages);
  return all;
};
 
const T = { position: "top-right", autoClose: 4000, hideProgressBar: false, closeOnClick: true, pauseOnHover: true, draggable: true, theme: "light", transition: Slide };
export const toastSuccess = (m) => toast.success(m, T);
export const toastError = (m) => toast.error(m, T);
 
export const ANY = { id: "any", name: "Any" };
export const DRAFT_ID = "__draft__";
export const MULTI_FIELDS = ["brand_master_ids", "product_codes"];
 
export const emptyFilterForm = {
  category_master_id: "",
  principal_master_id: "",
  brand_master_ids: [],
  product_codes: [],
  packsize: "",
  packtype: "",
  flavour: "",
  segment_1: "",
  segment_2: "",
  segment_3: "",
};
 
export const ATTRIBUTE_ROWS = [
  { key: "category_master_id", label: "category_master_id", required: true },
  { key: "principal_master_id", label: "principal_master_id", required: false },
  { key: "brand_master_ids", label: "brand_master_id", required: false },
  { key: "product_codes", label: "product_id", required: false },
  { key: "packsize", label: "packsize", required: false },
  { key: "packtype", label: "packtype", required: false },
  { key: "flavour", label: "flavour", required: false },
  { key: "segment_1", label: "segment_1", required: false, isSegment: true },
  { key: "segment_2", label: "segment_2", required: false, isSegment: true },
  { key: "segment_3", label: "segment_3", required: false, isSegment: true },
];
 
export const parseMulti = (val) => {
  if (Array.isArray(val)) return val.filter(Boolean).map(String);
  if (!val) return [];
  const inner = String(val).trim().replace(/^{/, "").replace(/}$/, "");
  if (!inner) return [];
  return inner.split(",").map((s) => s.trim()).filter(Boolean);
};
 
export const joinMulti = (arr) => (arr || []).filter(Boolean).join(",");
 
export const buildPayload = (f) => ({
  category_master_id: f.category_master_id ? String(f.category_master_id) : "",
  principal_master_id: f.principal_master_id ? String(f.principal_master_id) : "",
  brand_master_ids: joinMulti(f.brand_master_ids),
  product_codes: joinMulti(f.product_codes),
  packsize: f.packsize || "",
  packtype: f.packtype || "",
  flavour: f.flavour || "",
  segment_1: f.segment_1 || "",
  segment_2: f.segment_2 || "",
  segment_3: f.segment_3 || "",
});
 
 
const isAnySentinel = (term) => !term || term.trim().toLowerCase() === "any";
 
export function generateReportLineSQL(form) {
  const whereClauses = [];
 
  if (form.category_master_id) {
    whereClauses.push(`category_master_id=${form.category_master_id}`);
  }
 
  if (form.principal_master_id) {
    whereClauses.push(`principal_master_id=${form.principal_master_id}`);
  }
 
  const brandIds = [...new Set((form.brand_master_ids || []).map(String).map((s) => s.trim()).filter(Boolean))].sort();
  if (brandIds.length === 1) {
    whereClauses.push(`brand_master_id=${brandIds[0]}`);
  } else if (brandIds.length > 1) {
    whereClauses.push(`brand_master_id IN (${brandIds.join(", ")})`);
  }
 
  const productCodes = [...new Set((form.product_codes || []).map(String).map((s) => s.trim()).filter(Boolean))].sort();
  if (productCodes.length === 1) {
    whereClauses.push(`product_code=${productCodes[0]}`);
  } else if (productCodes.length > 1) {
    whereClauses.push(`product_code IN (${productCodes.join(", ")})`);
  }
 
  if (form.packsize) whereClauses.push(`packsize=${form.packsize}`);
  if (form.packtype) whereClauses.push(`packtype=${form.packtype}`);
  if (form.flavour) whereClauses.push(`flavour=${form.flavour}`);
  if (form.segment_1) whereClauses.push(`segment_1=${form.segment_1}`);
  if (form.segment_2) whereClauses.push(`segment_2=${form.segment_2}`);
  if (form.segment_3) whereClauses.push(`segment_3=${form.segment_3}`);
 
  if (whereClauses.length === 0) return "WHERE (1=1);";
  return `WHERE (${whereClauses.join(" AND ")});`;
}
 
export function useEntitySearch({ basePath, extraQuery = "", nameCacheRef, idKey, nameKey, dedupeById = false, enabled = true, lazy = false }) {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [triggered, setTriggered] = useState(!lazy);
  const notifyOpen = useCallback(() => setTriggered(true), []);
  const notifyClose = useCallback(() => { if (lazy) setTriggered(false); }, [lazy]);
 
  const toOptions = useCallback((rows) => {
    if (!dedupeById) return rows.map((row) => ({ id: String(row[idKey]), name: row[nameKey] }));
    const seen = new Set();
    const out = [];
    rows.forEach((row) => {
      const id = String(row[idKey]);
      if (seen.has(id)) return;
      seen.add(id);
      out.push({ id, name: row[nameKey] });
    });
    return out;
  }, [idKey, nameKey, dedupeById]);
 
  const reset = useCallback(() => {
    setInputValue("");
    setOptions([]);
    setPage(1);
    setHasMore(true);
    setLoading(false);
    setLoadingMore(false);
    if (lazy) setTriggered(false);
  }, [lazy]);
 
  useEffect(() => { setPage(1); }, [inputValue, extraQuery, basePath]);
 
  useEffect(() => {
    if (!enabled || !triggered) { setOptions([]); setHasMore(false); return; }
 
    // set loading true segera (bukan di dalam setTimeout) supaya
    // MUI Autocomplete langsung nunjukin spinner, gak nunggu debounce dulu
    setLoading(true);
 
    let cancelled = false;
    const h = setTimeout(async () => {
      try {
        const term = inputValue.trim();
        const searchQuery = isAnySentinel(term) ? "" : buildSearchQuery(term);
        const r = await apiFetch.get(`${basePath}?page=1&size=${SEARCH_PAGE_SIZE}${extraQuery}${searchQuery}`);
        const rows = r?.data || [];
        rows.forEach((row) => { nameCacheRef.current[String(row[idKey])] = row[nameKey]; });
        if (!cancelled) {
          setOptions(toOptions(rows));
          setHasMore((r?.pagination_info?.total_pages || 1) > 1);
        }
      } catch {
        if (!cancelled) { setOptions([]); setHasMore(false); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(h); };
  }, [inputValue, extraQuery, basePath, enabled, triggered, idKey, nameKey, nameCacheRef, toOptions]);
 
  const loadMore = useCallback(async () => {
    if (!enabled || !triggered || loadingMore || !hasMore) return;
    const nextPage = page + 1;
    setLoadingMore(true);
    try {
      const term = inputValue.trim();
      const searchQuery = isAnySentinel(term) ? "" : buildSearchQuery(term);
      const r = await apiFetch.get(`${basePath}?page=${nextPage}&size=${SEARCH_PAGE_SIZE}${extraQuery}${searchQuery}`);
      const rows = r?.data || [];
      rows.forEach((row) => { nameCacheRef.current[String(row[idKey])] = row[nameKey]; });
      setOptions((prev) => {
        if (!dedupeById) return [...prev, ...toOptions(rows)];
        const prevRaw = prev.map((o) => ({ [idKey]: o.id, [nameKey]: o.name }));
        return toOptions([...prevRaw, ...rows]);
      });
      setPage(nextPage);
      setHasMore(nextPage < (r?.pagination_info?.total_pages || 1));
    } catch {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [basePath, extraQuery, inputValue, page, hasMore, loadingMore, enabled, triggered, idKey, nameKey, nameCacheRef, dedupeById, toOptions]);
 
  const onInputChange = useCallback((_, v, reason) => {
    if (reason === "input") setInputValue(v);
  }, []);
 
  return { inputValue, setInputValue, onInputChange, options, loading, loadMore, hasMore, loadingMore, notifyOpen, notifyClose, reset };
}
 
export const makeListboxProps = (hook) => ({
  style: { maxHeight: 260 },
  onScroll: (e) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 32) hook.loadMore();
  },
});
 
export const withSelected = (options, selected) => {
  if (!selected) return options;
  const list = Array.isArray(selected) ? selected : [selected];
  const missing = list.filter(
    (v) => v && v.id !== undefined && v.id !== null && !options.some((o) => String(o.id) === String(v.id))
  );
  return missing.length ? [...missing, ...options] : options;
};